// CR036: Unclassified Queue Component

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, XCircle, Pencil, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { NormalizedDeal, DealType, DealStatus } from '@/lib/flyer-pipeline-v2/types';
import { toast } from 'sonner';

interface UnclassifiedQueueProps {
  deals: NormalizedDeal[];
  onUpdate: () => void;
}

const DEAL_TYPES: DealType[] = ['BONUS_GIFT_CARD', 'POINTS_BACK', 'DISCOUNT', 'UNCLASSIFIED'];

interface EditDialogState {
  isOpen: boolean;
  deal: NormalizedDeal | null;
}

export function UnclassifiedQueue({ deals, onUpdate }: UnclassifiedQueueProps) {
  const [editDialog, setEditDialog] = useState<EditDialogState>({ isOpen: false, deal: null });
  const [editedValues, setEditedValues] = useState<Partial<NormalizedDeal>>({});
  const [overrideReason, setOverrideReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async (deal: NormalizedDeal, newType?: DealType) => {
    setIsSubmitting(true);
    try {
      const updateData: Record<string, unknown> = {
        status: 'APPROVED',
        was_overridden: true,
        overridden_at: new Date().toISOString(),
        override_reason: 'Approved by admin',
      };

      if (newType && newType !== deal.deal_type) {
        updateData.deal_type = newType;
      }

      await supabase
        .from('flyer_normalized_deals')
        .update(updateData)
        .eq('id', deal.deal_id);

      // Log audit entry
      await supabase.from('flyer_deal_override_audit').insert({
        deal_id: deal.deal_id,
        action: 'APPROVE',
        before_values: { deal_type: deal.deal_type, status: deal.status },
        after_values: { deal_type: newType || deal.deal_type, status: 'APPROVED' },
        reason: 'Approved by admin',
      });

      toast.success('Deal approved');
      onUpdate();
    } catch (error) {
      console.error('Error approving deal:', error);
      toast.error('Failed to approve deal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = async (deal: NormalizedDeal) => {
    setIsSubmitting(true);
    try {
      await supabase
        .from('flyer_normalized_deals')
        .update({
          status: 'DISMISSED',
          was_overridden: true,
          overridden_at: new Date().toISOString(),
          override_reason: 'Dismissed by admin',
        })
        .eq('id', deal.deal_id);

      // Log audit entry
      await supabase.from('flyer_deal_override_audit').insert({
        deal_id: deal.deal_id,
        action: 'DISMISS',
        before_values: { status: deal.status },
        after_values: { status: 'DISMISSED' },
        reason: 'Dismissed by admin',
      });

      toast.success('Deal dismissed');
      onUpdate();
    } catch (error) {
      console.error('Error dismissing deal:', error);
      toast.error('Failed to dismiss deal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditDialog = (deal: NormalizedDeal) => {
    setEditDialog({ isOpen: true, deal });
    setEditedValues({
      deal_type: deal.deal_type,
      merchant: deal.merchant,
      purchase: { ...deal.purchase },
      reward: { ...deal.reward },
      validity: { ...deal.validity },
    });
    setOverrideReason('');
  };

  const handleSaveEdit = async () => {
    if (!editDialog.deal || !overrideReason.trim()) {
      toast.error('Please provide a reason for the override');
      return;
    }

    setIsSubmitting(true);
    try {
      const updateData: Record<string, unknown> = {
        deal_type: editedValues.deal_type,
        merchant: editedValues.merchant,
        purchase_brand: editedValues.purchase?.brand,
        purchase_amount: editedValues.purchase?.amount,
        reward_merchant: editedValues.reward?.merchant,
        reward_amount: editedValues.reward?.amount,
        validity_start_date: editedValues.validity?.start_date,
        validity_end_date: editedValues.validity?.end_date,
        status: 'APPROVED',
        was_overridden: true,
        overridden_at: new Date().toISOString(),
        override_reason: overrideReason,
        original_values: {
          deal_type: editDialog.deal.deal_type,
          merchant: editDialog.deal.merchant,
          purchase: editDialog.deal.purchase,
          reward: editDialog.deal.reward,
          validity: editDialog.deal.validity,
        },
      };

      await supabase
        .from('flyer_normalized_deals')
        .update(updateData)
        .eq('id', editDialog.deal.deal_id);

      // Log audit entry
      await supabase.from('flyer_deal_override_audit').insert({
        deal_id: editDialog.deal.deal_id,
        action: 'EDIT',
        before_values: {
          deal_type: editDialog.deal.deal_type,
          merchant: editDialog.deal.merchant,
          purchase: editDialog.deal.purchase,
          reward: editDialog.deal.reward,
        },
        after_values: editedValues,
        reason: overrideReason,
      });

      toast.success('Deal updated');
      setEditDialog({ isOpen: false, deal: null });
      onUpdate();
    } catch (error) {
      console.error('Error updating deal:', error);
      toast.error('Failed to update deal');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
        <h3 className="text-lg font-medium">All Clear!</h3>
        <p className="text-sm text-muted-foreground mt-1">
          No unclassified or review items in the queue.
        </p>
      </div>
    );
  }

  return (
    <>
      <ScrollArea className="h-[400px]">
        <div className="space-y-3 p-2">
          {deals.map((deal) => (
            <Card key={deal.deal_id} className="border-yellow-200 bg-yellow-50/30">
              <CardHeader className="py-2 px-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <CardTitle className="text-sm">Page {deal.page_no}</CardTitle>
                    <Badge variant="destructive">{deal.deal_type}</Badge>
                    <Badge variant="outline">{deal.status}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    Confidence: {(deal.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </CardHeader>
              <CardContent className="py-2 px-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Purchase</p>
                    <p>
                      {deal.purchase.brand || 'Unknown'} @ ${deal.purchase.amount || '?'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Reward</p>
                    <p>
                      ${deal.reward.amount || '?'} {deal.reward.merchant && `at ${deal.reward.merchant}`}
                    </p>
                  </div>
                </div>

                {/* Debug Notes */}
                {deal.debug.ai_notes.length > 0 && (
                  <div className="mt-2 p-2 bg-muted/50 rounded text-xs">
                    <p className="font-medium">AI Notes:</p>
                    <ul className="list-disc list-inside">
                      {deal.debug.ai_notes.map((note, i) => (
                        <li key={i}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 mt-3">
                  <Select
                    onValueChange={(value) => handleApprove(deal, value as DealType)}
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="h-8 w-40">
                      <SelectValue placeholder="Classify as..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DEAL_TYPES.filter((t) => t !== 'UNCLASSIFIED').map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => openEditDialog(deal)}
                    disabled={isSubmitting}
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-destructive"
                    onClick={() => handleDismiss(deal)}
                    disabled={isSubmitting}
                  >
                    <XCircle className="h-3 w-3 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* Edit Dialog */}
      <Dialog open={editDialog.isOpen} onOpenChange={(open) => !open && setEditDialog({ isOpen: false, deal: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Deal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Deal Type</Label>
              <Select
                value={editedValues.deal_type}
                onValueChange={(v) => setEditedValues({ ...editedValues, deal_type: v as DealType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEAL_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Purchase Brand</Label>
                <Input
                  value={editedValues.purchase?.brand || ''}
                  onChange={(e) =>
                    setEditedValues({
                      ...editedValues,
                      purchase: { ...editedValues.purchase!, brand: e.target.value },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Purchase Amount</Label>
                <Input
                  type="number"
                  value={editedValues.purchase?.amount || ''}
                  onChange={(e) =>
                    setEditedValues({
                      ...editedValues,
                      purchase: { ...editedValues.purchase!, amount: parseFloat(e.target.value) || 0 },
                    })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Reward Merchant</Label>
                <Input
                  value={editedValues.reward?.merchant || ''}
                  onChange={(e) =>
                    setEditedValues({
                      ...editedValues,
                      reward: { ...editedValues.reward!, merchant: e.target.value },
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Reward Amount</Label>
                <Input
                  type="number"
                  value={editedValues.reward?.amount || ''}
                  onChange={(e) =>
                    setEditedValues({
                      ...editedValues,
                      reward: { ...editedValues.reward!, amount: parseFloat(e.target.value) || 0 },
                    })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Override Reason *</Label>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Explain why you're making this change..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ isOpen: false, deal: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSubmitting || !overrideReason.trim()}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
